// The desktop Scenes page — every scene the user can reach, grouped by
// Location > Room, runnable from here.
//
// This replaces a hardcoded "No Scenes Available" placeholder that shipped
// before per-room Scenes existed: the feature was live, but this page still
// claimed there were none, so on desktop scenes were only findable inside a
// room. Mobile's Scenes tab reads the same core loader, so the two agree.
//
// Run is available to everyone with access to the room. Editing lives on the
// room page (owner only) — this is a launcher, not an editor.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, AppBar, Toolbar, IconButton, CircularProgress, Card,
  CardActionArea, Grid, Divider, Button, Snackbar, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../core/state/AppContext';
import { fetchScenesTree, countScenes } from '../../../core/scenes/allScenes';
import { runScene } from './scenes/scenesApi';

export default function ScenesScreen() {
  const navigate = useNavigate();
  const { userId, locations } = useApp();

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTree(await fetchScenesTree(userId, locations).catch(() => []));
    } finally {
      setLoading(false);
    }
  }, [userId, locations]);

  useEffect(() => { load(); }, [load]);

  const run = async (scene) => {
    if (runningId) return;
    setRunningId(scene.id);
    setToast(`Running "${scene.name}"…`);
    try {
      await runScene(userId, scene);
      setToast(`Ran "${scene.name}"`);
    } finally {
      setRunningId(null);
    }
  };

  const total = countScenes(tree);

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>Scenes</Typography>
          <IconButton color="inherit" onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : total === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
          <Typography color="text.secondary" variant="h6">No scenes yet</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ maxWidth: 460, textAlign: 'center' }}>
            Scenes are created per room by the location owner — open a room and
            right-click the Scenes strip to add one.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {tree.map(({ location, rooms }) => {
            const roomsWithScenes = rooms.filter((r) => r.scenes.length > 0);
            if (roomsWithScenes.length === 0) return null;
            return (
              <Box key={location.LocationID} sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {location.LocationName || 'Location'}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                {roomsWithScenes.map(({ room, scenes }) => (
                  <Box key={room.RoomID} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MeetingRoomIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="subtitle2" color="text.primary">
                        {room.RoomName || `Room ${room.RoomID}`}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        onClick={() => navigate(`/room/${room.RoomID}/scenes`, {
                          state: { name: room.RoomName, locationId: location.LocationID },
                        })}
                      >
                        Open room
                      </Button>
                    </Box>

                    <Grid container spacing={1.5}>
                      {scenes.map((s) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={s.id}>
                          <Tooltip title="Click to run">
                            <Card sx={{
                              bgcolor: s.color,
                              borderRadius: 3,
                              opacity: runningId && runningId !== s.id ? 0.5 : 1,
                            }}
                            >
                              <CardActionArea
                                onClick={() => run(s)}
                                disabled={!!runningId}
                                sx={{ p: 1.5, minHeight: 84 }}
                              >
                                <Box sx={{
                                  display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', gap: 0.5, color: s.textColor,
                                }}
                                >
                                  {runningId === s.id
                                    ? <CircularProgress size={20} sx={{ color: s.textColor }} />
                                    : <AutoAwesomeIcon />}
                                  <Typography fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>
                                    {s.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                    {runningId === s.id
                                      ? 'Running…'
                                      : `${s.steps.length} step${s.steps.length === 1 ? '' : 's'}`}
                                  </Typography>
                                </Box>
                              </CardActionArea>
                            </Card>
                          </Tooltip>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
